import { useEffect, useMemo } from "react";
import ReactMarkdown from "react-markdown";
import {
  useFlagsStatus,
  useVariant,
} from "@unleash/proxy-client-react";

import {
  featurePageCacheSchema,
  featurePageSchema,
  parseFeaturePage,
  resolveFeaturePageFlagName,
  type FeaturePage,
} from "../lib/feature-page";
import { FeaturePageRenderer } from "./FeaturePageRenderer";
import "./InformationPageContent.css";

const DEFAULT_CACHE_NAMESPACE = "petcollab:feature-page:v1";
const memoryCache = new Map<string, FeaturePage>();

export interface InformationPageContentProps {
  /** English Unleash flag name, for example `information.about`. */
  flagName: string;
  /** BCP 47 locale. Spanish locales resolve to `${flagName}.es`. */
  locale: string;
  /** Bundled content used when remote content is unavailable or invalid. */
  fallback: FeaturePage;
  /** Must only be true when the app is inside a configured FlagProvider. */
  remoteEnabled?: boolean;
  className?: string;
  cacheNamespace?: string;
}

function cacheKey(namespace: string, flagName: string) {
  return `${namespace}:${encodeURIComponent(flagName)}`;
}

function readCachedPage(key: string): FeaturePage | undefined {
  const memoryValue = memoryCache.get(key);

  if (memoryValue) {
    return memoryValue;
  }

  if (typeof window === "undefined") {
    return undefined;
  }

  try {
    const rawValue = window.localStorage.getItem(key);

    if (!rawValue) {
      return undefined;
    }

    const result = featurePageCacheSchema.safeParse(JSON.parse(rawValue) as unknown);

    if (!result.success) {
      return undefined;
    }

    memoryCache.set(key, result.data.page);
    return result.data.page;
  } catch {
    return undefined;
  }
}

function writeCachedPage(key: string, page: FeaturePage) {
  memoryCache.set(key, page);

  if (typeof window === "undefined") {
    return;
  }

  try {
    window.localStorage.setItem(
      key,
      JSON.stringify({ version: 1, page }),
    );
  } catch {
    // Storage can be unavailable in private browsing or blocked by policy.
  }
}

interface RemoteInformationPageContentProps {
  flagName: string;
  fallback: FeaturePage;
  className?: string;
  cacheNamespace: string;
}

interface PreparedInformationDocument {
  body: FeaturePage;
  eyebrow?: string;
  title?: string;
}

function mapPageChildren(
  page: FeaturePage,
  transform: (child: FeaturePage) => FeaturePage,
) {
  if (page.columns) {
    return { ...page, columns: page.columns.map(transform) };
  }

  if (page.rows) {
    return { ...page, rows: page.rows.map(transform) };
  }

  if (page.grid) {
    return {
      ...page,
      grid: { ...page.grid, content: page.grid.content.map(transform) },
    };
  }

  return page;
}

/**
 * The shared iOS model calls section headings `subtitle`, but an information
 * page needs an h1 followed by h2 sections on the web. The data remains the
 * same; only the renderer-facing copy is promoted to the correct HTML level.
 */
function promoteSectionHeadings(page: FeaturePage): FeaturePage {
  const pageWithChildren = mapPageChildren(page, promoteSectionHeadings);

  if (pageWithChildren.viewType !== "subtitle") {
    return pageWithChildren;
  }

  return {
    ...pageWithChildren,
    level: Math.max(2, pageWithChildren.level ?? 2),
    viewType: "title",
  };
}

function prepareInformationDocument(page: FeaturePage): PreparedInformationDocument {
  const collection = page.columns ?? page.rows;

  if (!collection) {
    return { body: page };
  }

  const titleIndex = collection.findIndex(
    (child) => child.viewType === "title" && Boolean(child.content?.trim()),
  );

  if (titleIndex < 0) {
    return { body: page };
  }

  const title = collection[titleIndex]?.content?.trim();
  let eyebrowIndex = -1;

  for (let index = titleIndex - 1; index >= 0; index -= 1) {
    const child = collection[index];

    if (child?.viewType === "caption" && child.content?.trim()) {
      eyebrowIndex = index;
      break;
    }
  }
  const eyebrow =
    eyebrowIndex >= 0 ? collection[eyebrowIndex]?.content?.trim() : undefined;
  const bodyCollection = collection
    .filter((_, index) => index !== titleIndex && index !== eyebrowIndex)
    .map(promoteSectionHeadings);
  const body = page.columns
    ? { ...page, columns: bodyCollection }
    : { ...page, rows: bodyCollection };

  return { body, eyebrow, title };
}

function documentHeadingId(pageId: string) {
  const safeId = pageId.toLowerCase().replace(/[^a-z0-9_-]+/g, "-");
  return `information-document-${safeId || "page"}-title`;
}

function InlineHeading({ children }: { children: string }) {
  return (
    <ReactMarkdown
      allowedElements={["p", "strong", "em", "del", "code", "br"]}
      components={{ p: ({ children: content }) => <>{content}</> }}
      skipHtml
      unwrapDisallowed
    >
      {children}
    </ReactMarkdown>
  );
}

interface InformationDocumentProps {
  page: FeaturePage;
  className?: string;
  contentState: string;
}

function InformationDocument({
  page,
  className,
  contentState,
}: InformationDocumentProps) {
  const document = useMemo(() => prepareInformationDocument(page), [page]);
  const headingId = document.title ? documentHeadingId(page.id) : undefined;
  const rendererClassName = ["information-document__content", className]
    .filter(Boolean)
    .join(" ");

  return (
    <article
      aria-labelledby={headingId}
      className="information-document"
    >
      {(document.eyebrow || document.title) && (
        <header className="information-document__header">
          {document.eyebrow && (
            <p className="information-document__eyebrow">
              <InlineHeading>{document.eyebrow}</InlineHeading>
            </p>
          )}
          {document.title && (
            <h1 className="information-document__title" id={headingId}>
              <InlineHeading>{document.title}</InlineHeading>
            </h1>
          )}
        </header>
      )}
      <FeaturePageRenderer
        className={rendererClassName}
        data-content-state={contentState}
        page={document.body}
      />
    </article>
  );
}

/** This child is mounted only when a configured Unleash provider is present. */
function RemoteInformationPageContent({
  flagName,
  fallback,
  className,
  cacheNamespace,
}: RemoteInformationPageContentProps) {
  const variant = useVariant(flagName);
  const { flagsError, flagsReady } = useFlagsStatus();
  const storageKey = cacheKey(cacheNamespace, flagName);
  const remotePayload = variant.payload?.value;
  const remoteResult =
    variant.enabled && typeof remotePayload === "string"
      ? parseFeaturePage(remotePayload)
      : undefined;
  const remotePage = remoteResult?.success ? remoteResult.data : undefined;
  const cachedPage = readCachedPage(storageKey);

  useEffect(() => {
    if (!variant.enabled || typeof remotePayload !== "string") {
      return;
    }

    const result = parseFeaturePage(remotePayload);

    if (result.success) {
      writeCachedPage(storageKey, result.data);
    }
  }, [remotePayload, storageKey, variant.enabled]);

  // A deliberate, successfully fetched disabled flag returns bundled content;
  // cached content is reserved for loading, network failure, or an enabled
  // variant with a missing/malformed payload.
  const mayUseCache =
    !flagsReady || Boolean(flagsError) || (variant.enabled && !remotePage);
  const usableCachedPage = mayUseCache ? cachedPage : undefined;
  const page = remotePage ?? usableCachedPage ?? fallback;
  const contentState = remotePage
    ? "remote"
    : usableCachedPage
      ? flagsError
        ? "cached-network-error"
        : flagsReady
          ? "cached-invalid-remote"
          : "cached-loading"
      : flagsError
        ? "fallback-network-error"
        : flagsReady
          ? "fallback"
          : "fallback-loading";

  return (
    <InformationDocument
      className={className}
      contentState={contentState}
      page={page}
    />
  );
}

export function InformationPageContent({
  flagName,
  locale,
  fallback,
  remoteEnabled = false,
  className,
  cacheNamespace = DEFAULT_CACHE_NAMESPACE,
}: InformationPageContentProps) {
  const fallbackResult = featurePageSchema.safeParse(fallback);

  if (!fallbackResult.success) {
    return null;
  }

  const resolvedFlagName = resolveFeaturePageFlagName(flagName, locale);

  if (!remoteEnabled) {
    return (
      <InformationDocument
        className={className}
        contentState="fallback-remote-disabled"
        page={fallbackResult.data}
      />
    );
  }

  return (
    <RemoteInformationPageContent
      cacheNamespace={cacheNamespace}
      className={className}
      fallback={fallbackResult.data}
      flagName={resolvedFlagName}
      key={resolvedFlagName}
    />
  );
}
